import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { catchError, filter, map, Observable, of, switchMap, withLatestFrom } from 'rxjs';

import * as DataAccessProductsActions from './data-access-products.actions';
import * as DataAccessProductsReducer from './data-access-products.reducer';
import * as DataAccessProductsSelectors from './data-access-products.selectors';

import { DataAccessExhibitorService } from '../../../services/exhibitors/data-access-exhibitors.service';
import { DataAccessProductsService } from '../../../services/products/data-access-products.service';
import * as DataAccessExhibitorsActions from '../../exhibitors/entity/data-access-exhibitors.actions';
import { DataAccessFairsFacade } from '../../fairs/entity/data-access-fairs.facade';
import * as DataAccessFairsSelectors from '../../fairs/entity/data-access-fairs.selectors';
import * as DataAccessPostsActions from '../../posts/entity/data-access-posts.actions';

@Injectable()
export class DataAccessProductsEffects {
  loadProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessProductsActions.productOpened),
      withLatestFrom(this.store.pipe(select(DataAccessProductsSelectors.getProduct))),
      withLatestFrom(this.store.pipe(select(DataAccessFairsSelectors.getActiveFairId))),
      filter(([[_, selectedProduct], __]) => !selectedProduct),
      switchMap(([[action, _], fair_id]) =>
        this.productsService.getProduct(fair_id, action.productUuid).pipe(
          map((result) => {
            return DataAccessProductsActions.productLoadSucceeded({
              product: result,
              fairId: fair_id,
            });
          }),
          catchError((err) => of(DataAccessProductsActions.productLoadFailed({ error: err.status.toString() })))
        )
      )
    )
  );

  loadPostProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessPostsActions.postLoadSucceeded),
      withLatestFrom(this.store.pipe(select(DataAccessProductsSelectors.getProductIds))),
      map(([action, loadedProducts]) =>
        (action.post?.products ?? []).filter((productId: string) => !loadedProducts.includes(productId))
      ),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([productsToLoad, fairId]) =>
        this.productsService.getProductsByIds(fairId, productsToLoad).pipe(
          map((postProductsResponse) =>
            // TODO: Only load 16. If more exist set indicator
            DataAccessProductsActions.productsLoadSucceeded({
              products: postProductsResponse,
            })
          ),
          catchError((err) => of(DataAccessProductsActions.productsLoadFailed({ error: err.status.toString() })))
        )
      )
    )
  );

  loadProductProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessProductsActions.productLoadSucceeded),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([action, fairId]) =>
        this.exhibitorService.getExhibitorProducts(fairId, action.product.exhibitor_id).pipe(
          map((exhibitorProductsResponse) =>
            DataAccessExhibitorsActions.exhibitorProductsLoadSucceeded({
              exhibitorProductsResponse: exhibitorProductsResponse,
              exhibitorId: action.product.exhibitor_id,
              fairId: action.fairId ?? 'unknown',
            })
          )
        )
      )
    )
  );

  fairId$: Observable<string>;

  constructor(
    private readonly actions$: Actions,
    private store: Store<DataAccessProductsReducer.ProductsState>,
    private productsService: DataAccessProductsService,
    private exhibitorService: DataAccessExhibitorService,
    private fairsFacade: DataAccessFairsFacade
  ) {
    this.fairId$ = this.fairsFacade.fairId$;
  }
}
